import React, { Component } from 'react';
import PropTypes from 'prop-types';
import validator from '../../utils/validator';
import { EMPTY as EMPTY_ERROR } from '../../data/enums/ErrorMessage';
import { LabelStyled, MessageStyled, TextStyled } from './TextIndex';
import { Icon } from '../../v4/components';
import { fixedFloat } from '../../utils/conversion';

const propTypes = {
  /**
   * @default null
   * children passed within button
   */
  children: PropTypes.node,
  /**
   * @default ''
   * children passed along with the button
   */
  className: PropTypes.string,
  /**
   * @default true
   * enable error display mode in the form
   */
  enableErrorDisplay: PropTypes.bool,
  /**
   * @default true
   * enable validation in the form
   */
  enableValidation: PropTypes.bool,
  /**
   * @default ''
   * error Message to display.
   */
  errorMessage: PropTypes.string,
  /**
   * @default ''
   * rule for validation: match , isEmail, ...
   */
  rule: PropTypes.string,
  /**
   * @default argument
   * @argument : extra rule against validation.
   * additional argument to be provided as rule. {min: 5, max: 25}
   */
  argument: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(Object)]),
  /**
   * @default false
   * if the input is valid or not
   */
  placeholder: PropTypes.string,
  /**
   * @default ''
   * value passed to the input text
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * @default null
   * function in HOC for input change
   */
  onChange: PropTypes.func,
  /**
   * name of the input field
   */
  name: PropTypes.string,
  /**
   * type of the input
   */
  type: PropTypes.string.isRequired,
  externalValidation: PropTypes.bool,
  autoComplete: PropTypes.string,
  displayMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool,
};

const defaultProps = {
  children: undefined,
  className: '',
  rule: '',
  argument: '',
  placeholder: 'Placeholder',
  enableErrorDisplay: false,
  enableValidation: true,
  value: '',
  onChange: () => null,
  errorMessage: EMPTY_ERROR,
  externalValidation: true,
  autoComplete: 'off',
  displayMessage: true,
  disabled: false,
  name: '',
};
const floatLengthRegex = '(^(?:-?\\d+).\\d{4,})'; /** digit after decimal equal or greater than 4* */

class Input extends Component {
  constructor(props) {
    super(props);
    this.state = {
      valid: false,
      nextValue: 0 || props.value,
    };
  }

  componentDidMount() {
    this.checkIfFieldIsValid(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.checkIfFieldIsValid(nextProps);
  }

  checkIfFieldIsValid = props => {
    const { rule, argument, type, value, externalValidation } = props;
    const valid = validator(rule, argument, type, value) && externalValidation;
    this.setState({ valid });
  };

  getValidState = () => {
    const { valid } = this.state;
    return valid;
  };

  getFormattedValue = (event, allowMultiZeroBeforeNumber, decimalWithPrecedingZero) => {
    const { type, value } = event;
    const { rule } = this.props;
    if (type === 'number') {
      if (allowMultiZeroBeforeNumber) {
        return value;
      }
      if (rule === 'isFloat') {
        const v = value.replace(/^0+(?=\d)/, '');
        if (decimalWithPrecedingZero) {
          return v.match(floatLengthRegex) ? fixedFloat(v) : v;
        }

        return fixedFloat(v);
      }
      if (decimalWithPrecedingZero) {
        return value.replace(/^0+(?=\d)/, '');
      }
      return Number(value.replace(/^0+(?=\d)/, ''));
    }
    return value;
  };

  handleChange = event => {
    const { onChange, allowMultiZeroBeforeNumber, decimalWithPrecedingZero } = this.props;
    event.formattedValue = this.getFormattedValue(event.target, allowMultiZeroBeforeNumber, decimalWithPrecedingZero);
    this.setState({ nextValue: event.formattedValue });
    onChange(event);
  };

  render() {
    const {
      placeholder,
      enableValidation,
      enableErrorDisplay,
      errorMessage,
      onChange,
      value,
      type,
      name,
      iconName,
      status,
      labelContent,
      autoComplete,
      displayMessage,
      decimalOnAppendString,
      disabled,
      rule,
      argument,
      ...props
    } = this.props;
    const { valid, nextValue } = this.state;
    const isError = enableValidation && enableErrorDisplay && !valid;
    const min = type === 'number' ? { min: '0' } : {};
    const max = type === 'number' ? { max: argument.max } : {};
    const derivedValue = decimalOnAppendString ? nextValue : value;
    return (
      <div
        className={`${valid ? 'form-input error-input' : 'form-input'} ${isError ? 'error-msg' : ''} form-input-${
          labelContent ? labelContent.toLowerCase() : ''
        }`}
      >
        {labelContent !== undefined ? <LabelStyled>{labelContent}</LabelStyled> : ''}
        <TextStyled
          placeholder={placeholder}
          onChange={e => this.handleChange(e)}
          value={value ? derivedValue.toString() : value}
          type={type}
          name={name}
          {...props}
          {...min}
          {...max}
          status={status}
          disabled={disabled}
          onWheel={e => e.target.blur()}
          errorBorder={isError && !displayMessage}
          autoComplete={autoComplete}
        />
        {isError && displayMessage && (
          <MessageStyled className="form-error" status={status}>
            {iconName !== undefined ? <Icon iconName={iconName} /> : ''}
            <span className="error-message">
              {errorMessage && <Icon iconName="exclamation-full" />}
              {errorMessage}
            </span>
          </MessageStyled>
        )}
      </div>
    );
  }
}

Input.propTypes = propTypes;

Input.defaultProps = defaultProps;

export default Input;
